import type { ContextDto } from '@dbrain/shared';
import { Button } from '@dbrain/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { keys, useContext } from '../../api/hooks.js';
import { contextRows, type ContextRowKey } from '@dbrain/app-core/context-rows';
import type { ContextUsage } from '@dbrain/app-core/conversation-state';
import { formatCount, formatPercent, formatTokens, tokensLocative } from '@dbrain/app-core/format';

/**
 * The context button and its small dialog (QW-301, QW-611, QW-1217; BaglamNormal, BaglamDolu, D-120), the same in
 * CompanyGPT and CompanyCode: one line with how much of the window is used and the percentage, a thin bar for the
 * whole window (a part per row, the compaction reserve hatched from the auto-summary threshold, a line at the
 * warning), one status line, five rows whose description shows on hover, the conversation's totals and the
 * actions: "Şimdi özetle", past the warning with "Yeni sohbet". The rows always add up to the header and the bar.
 * The button turns amber from the warning and red where the automatic summary is due.
 */

type Level = 'ok' | 'warn' | 'high';

/** the gap kept between the dialog and the edges of the column it opens in */
const EDGE = 8;

/** the nearest ancestor that clips its content (the conversation's column), or the window */
function clipBounds(node: HTMLElement): { left: number; right: number } {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (getComputedStyle(el).overflowX !== 'visible') {
      const rect = el.getBoundingClientRect();
      return { left: rect.left, right: rect.right };
    }
  }
  return { left: 0, right: window.innerWidth };
}

export function ContextRing({
  conversationId,
  live,
  running,
  onCompact,
  onNewConversation,
}: {
  conversationId: string;
  live: ContextUsage | null;
  running: boolean;
  onCompact: () => void;
  onNewConversation: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const context = useContext(conversationId, true);
  const lastRunning = useRef(running);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const titleId = useId();
  const statusId = useId();
  // a finished turn changes the numbers: read them again
  useEffect(() => {
    if (lastRunning.current && !running) void queryClient.invalidateQueries({ queryKey: keys.context(conversationId) });
    lastRunning.current = running;
  }, [running, conversationId, queryClient]);
  // opened: focus in the dialog; a click elsewhere closes it
  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onPointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);
  // opened: the dialog stays inside the column that clips it. On a narrower window the bar's controls wrap to
  // the left, and a dialog aligned to the button's right edge would run under the sidebar.
  useLayoutEffect(() => {
    const node = panel.current;
    if (!open || !node) return;
    const place = () => {
      node.style.removeProperty('--ctx-shift');
      const bounds = clipBounds(node);
      node.style.setProperty('--ctx-max-width', `${Math.round(bounds.right - bounds.left - 2 * EDGE)}px`);
      const rect = node.getBoundingClientRect();
      if (rect.left < bounds.left + EDGE) node.style.setProperty('--ctx-shift', `${Math.round(bounds.left + EDGE - rect.left)}px`);
      node.style.setProperty('--ctx-max-height', `${Math.max(240, Math.round(window.innerHeight - rect.top - EDGE))}px`);
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);
  const data: ContextDto | undefined = context.data;
  // holds its place while the numbers load, so the bar's other controls do not move (D-79)
  if (!data) return <span className="context-ring__placeholder" aria-hidden="true" />;
  const locale = i18n.language;
  // every number from one source: what this page received live with its breakdown, else the stored (or estimated)
  // one; a turn's bare total replayed after a reload is the same request the stored breakdown describes
  const source = live && live.categories.length > 0 ? live : null;
  const size = source?.contextWindow || data.contextWindow;
  const prompt = source ? source.promptTokens : data.promptTokens;
  const breakdown = contextRows(prompt, source ? source.categories : data.categories);
  const loaded = (source ? source.skills : data.skills) ?? [];
  const { warn, auto } = data.thresholds;
  const ratio = size > 0 ? Math.min(1, breakdown.total / size) : 0;
  const level: Level = breakdown.total >= auto ? 'high' : breakdown.total >= warn ? 'warn' : 'ok';
  const percent = formatPercent(breakdown.total, size, locale);
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  // the bar on a 1000-wide scale (an SVG: no inline styles under the CSP)
  const x = (value: number) => (size > 0 ? Math.min(1000, (value / size) * 1000) : 0);
  let cursor = 0;
  const segments = breakdown.rows
    .filter((row) => row.tokens > 0)
    .map((row) => {
      const start = cursor;
      cursor += row.tokens;
      return { key: row.key, x: x(start), end: x(cursor), width: Math.max(1.5, x(cursor) - x(start)) };
    });
  const circumference = 2 * Math.PI * 9;
  const about = (key: ContextRowKey) => {
    if (key === 'system' && breakdown.extras.length > 0) return t('context.about.systemWith', { list: breakdown.extras.map((extra) => t(`context.extras.${extra}`)).join(', ') });
    // QW-1217: each loaded skill with its share of the prompt
    if (key === 'skills' && loaded.length > 0) return t('context.about.skillsLoaded', { list: loaded.map((skill) => `${skill.name} ${formatTokens(skill.tokens, locale)}`).join(', ') });
    return t(`context.about.${key}`);
  };
  const autoText = formatTokens(auto, locale);
  return (
    <div ref={wrapper} className="context-ring" data-testid="context-ring" data-level={level}>
      <button
        ref={button}
        type="button"
        className={`context-ring__button context-ring__button--${level}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-label={t('context.label', { percent: Math.round(ratio * 100) })}
        onClick={() => setOpen((value) => !value)}
        data-testid="context-ring-button"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <circle cx="12" cy="12" r="9" className="context-ring__track" fill="none" strokeWidth="3" />
          <circle
            cx="12"
            cy="12"
            r="9"
            className={`context-ring__value context-ring__value--${level}`}
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${(ratio * circumference).toFixed(2)} ${circumference.toFixed(2)}`}
            transform="rotate(-90 12 12)"
          />
        </svg>
        <span className="context-ring__percent">{percent}</span>
      </button>
      {open ? (
        <div
          ref={panel}
          id={dialogId}
          className={`context-panel context-panel--${level}`}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          aria-describedby={statusId}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close(true);
            }
          }}
          data-testid="context-panel"
        >
          <header className="context-panel__head">
            <h2 id={titleId} className="context-panel__title">
              {t('context.title')}
            </h2>
            <span
              className="context-panel__used"
              title={data.estimated && !source ? t('context.estimated') : undefined}
              data-testid="context-used"
              data-total={breakdown.total}
            >
              {t('context.used', { used: formatTokens(breakdown.total, locale), window: formatTokens(size, locale) })}
            </span>
            <span className={`context-panel__percent context-panel__percent--${level}`} data-testid="context-percent">
              {percent}
            </span>
          </header>
          <svg
            className="context-bar"
            viewBox="0 0 1000 8"
            preserveAspectRatio="none"
            role="img"
            aria-label={t('context.barLabel', { percent })}
            data-testid="context-bar"
            data-filled={x(breakdown.total).toFixed(3)}
          >
            <defs>
              <pattern id={`${dialogId}-hatch`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" className="context-bar__reserve-bg" />
                <line x1="0" y1="0" x2="0" y2="6" className="context-bar__reserve-line" strokeWidth="2.5" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="1000" height="8" className="context-bar__track" />
            <rect x={x(auto)} y="0" width={1000 - x(auto)} height="8" fill={`url(#${dialogId}-hatch)`} />
            {segments.map((segment) => (
              <rect
                key={segment.key}
                x={segment.x}
                y="0"
                width={segment.width}
                height="8"
                className={`context-bar__part context-bar__part--${segment.key}`}
                data-testid="context-bar-part"
                data-end={segment.end.toFixed(3)}
              />
            ))}
            <rect x={Math.max(0, x(warn) - 3)} y="0" width="6" height="8" className="context-bar__warn" />
          </svg>
          <p id={statusId} className={`context-status context-status--${level}`} data-testid="context-status">
            <span className="context-status__dot" aria-hidden="true" />
            <span>
              {level === 'ok' ? (
                t('context.status.ok', { auto: autoText, ending: tokensLocative(autoText) })
              ) : level === 'warn' ? (
                <Trans i18nKey="context.status.warn" values={{ left: formatTokens(Math.max(0, auto - breakdown.total), locale) }} components={{ b: <strong /> }} />
              ) : (
                <Trans i18nKey="context.status.high" components={{ b: <strong /> }} />
              )}
            </span>
          </p>
          <ul className="context-rows">
            {breakdown.rows.map((row) => {
              const text = about(row.key);
              return (
                <li key={row.key} className="context-row" title={text} data-testid="context-category" data-key={row.key} data-tokens={row.tokens}>
                  <span className={`context-swatch context-swatch--${row.key}`} aria-hidden="true" />
                  <span className="context-row__name">
                    {t(`context.categories.${row.key}`)}
                    <span className="visually-hidden">: {text}</span>
                  </span>
                  <span className="context-row__value">{formatTokens(row.tokens, locale)}</span>
                </li>
              );
            })}
          </ul>
          <footer className="context-panel__foot">
            <p className="context-panel__session">
              {t('context.session', {
                input: formatTokens(data.session.inputTokens, locale),
                output: formatTokens(data.session.outputTokens, locale),
                calls: formatCount(data.session.modelCalls, locale),
                count: data.session.modelCalls,
              })}
            </p>
            <div className="context-panel__actions">
              {level !== 'ok' ? (
                <Button size="sm" variant="secondary" onClick={onNewConversation} data-testid="context-new">
                  {t('context.newConversation')}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant={level === 'ok' ? 'secondary' : 'primary'}
                title={t('context.compactHint')}
                disabled={running}
                onClick={() => {
                  onCompact();
                  close(true);
                }}
                data-testid="context-compact"
              >
                {t('context.compact')}
              </Button>
            </div>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
